import React, { useEffect, useRef, useState } from 'react';
import cn from '@/utils/cn';
import type { SPANavSwitcher } from './SPANav';

interface SPANavSwitcherControlProps {
  // Changes here (e.g. the route) close the panel: the pick landed, its job is done.
  closeOn?: string;
  switcher: SPANavSwitcher;
  title: React.ReactNode;
}

// The entity switcher at the top of a drill rail: the full title as a button, opening a searchable
// panel of siblings. Shared by the SPA rail (SPANav) and the legacy content rail. Keyboard: arrow
// down on the title opens the panel; in the search box arrows move the highlighted option and
// Enter goes there, as the page crumbs' menus do.
const SPANavSwitcherControl = ({ closeOn, switcher, title }: SPANavSwitcherControlProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOpen(false);
    setQuery('');
  }, [closeOn]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      setOpen(false);
      setQuery('');
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [open]);

  const filteredOptions = switcher.options.filter(
    option => option.label.toLowerCase().includes(query.toLowerCase()),
  );

  // The highlight starts on the current option (or the first match once a query narrows the list).
  useEffect(() => {
    const currentIndex = query ? -1 : filteredOptions.findIndex(option => option.value === switcher.value);
    setActiveIndex(currentIndex === -1 ? 0 : currentIndex);
  }, [open, query, switcher.options, switcher.value]);

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>('[data-active]')?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, open]);

  const closeOnEscape = (event: React.KeyboardEvent) => {
    if (event.key !== 'Escape' || !open) return;
    event.stopPropagation();
    setOpen(false);
    setQuery('');
  };

  const openOnArrowDown = (event: React.KeyboardEvent) => {
    closeOnEscape(event);
    if (event.key === 'ArrowDown' && !open) {
      event.preventDefault();
      setOpen(true);
    }
  };

  const moveOrPick = (event: React.KeyboardEvent) => {
    closeOnEscape(event);
    const count = filteredOptions.length;
    if (event.key === 'ArrowDown' && count) {
      event.preventDefault();
      setActiveIndex(index => (index + 1) % count);
    } else if (event.key === 'ArrowUp' && count) {
      event.preventDefault();
      setActiveIndex(index => (index - 1 + count) % count);
    } else if (event.key === 'Enter' && filteredOptions[activeIndex]) {
      event.preventDefault();
      switcher.onSelect(filteredOptions[activeIndex].value);
    }
  };

  return (
    <div ref={containerRef} className="tw-relative tw-flex tw-min-w-0 tw-flex-col">
      <button
        aria-expanded={open}
        className={cn(
          '-tw-mx-1.5 tw-flex tw-min-w-0 tw-cursor-pointer tw-items-baseline tw-gap-1.5',
          'tw-rounded-md tw-border-0 tw-bg-transparent tw-px-1.5 tw-py-1 tw-text-left tw-text-sm',
          'tw-font-bold tw-leading-5 tw-text-spa-heading hover:tw-bg-spa-bg-tertiary',
        )}
        type="button"
        onClick={() => setOpen(current => !current)}
        onKeyDown={openOnArrowDown}
      >
        <span className="tw-min-w-0 tw-whitespace-normal tw-break-words">{title}</span>
        <span aria-hidden="true" className="tw-text-[10px] tw-text-spa-tertiary">▾</span>
      </button>
      {open && (
        <div
          className={cn(
            'tw-absolute tw-left-0 tw-right-0 tw-top-full tw-z-20 tw-mt-1 tw-box-border tw-rounded-lg',
            'tw-border tw-border-solid tw-border-spa-accent tw-bg-spa-bg-secondary tw-p-2 tw-shadow-xl',
          )}
        >
          <input
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            className={cn(
              'tw-mb-1 tw-box-border tw-w-full tw-rounded-md tw-border tw-border-solid',
              'tw-border-spa-border-input tw-bg-spa-bg-tertiary tw-px-3 tw-py-2 tw-text-sm',
              'tw-text-spa-on-bg tw-outline-none tw-placeholder-spa-secondary',
              'focus:tw-border-spa-accent focus:tw-ring-1 focus:tw-ring-spa-accent',
            )}
            placeholder={switcher.placeholder}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={moveOrPick}
          />
          <div ref={listRef} className="tw-max-h-72 tw-overflow-y-auto">
            {switcher.loading && (
              <div aria-hidden="true" className="tw-animate-pulse">
                {['tw-w-48', 'tw-w-40', 'tw-w-52'].map(width => (
                  <div
                    key={width}
                    className={cn('tw-mx-2 tw-my-3 tw-h-3.5 tw-rounded tw-bg-spa-bg-skeleton-subtle', width)}
                  />
                ))}
              </div>
            )}
            {filteredOptions.map((option, index) => (
              <button
                key={option.value}
                className={cn(
                  // Program titles run long — wrap like the reuse-program dropdown's options
                  // instead of truncating.
                  'tw-block tw-w-full tw-cursor-pointer tw-whitespace-normal tw-break-words',
                  'tw-rounded-md tw-border-0 tw-bg-transparent tw-px-2 tw-py-1.5 tw-text-left tw-text-sm',
                  option.value === switcher.value
                    ? 'tw-font-bold tw-text-spa-accent-text'
                    : 'tw-text-spa-primary',
                  index === activeIndex && 'tw-bg-spa-bg-tertiary',
                )}
                data-active={index === activeIndex || undefined}
                type="button"
                onClick={() => switcher.onSelect(option.value)}
                onKeyDown={closeOnEscape}
                onMouseEnter={() => setActiveIndex(index)}
              >
                {option.value === switcher.value ? `✓ ${option.label}` : option.label}
              </button>
            ))}
            {!switcher.loading && filteredOptions.length === 0 && (
              <p className="tw-m-0 tw-px-2 tw-py-1.5 tw-text-sm tw-text-spa-secondary">No matches.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SPANavSwitcherControl;
